import { fabric } from 'fabric';

const WIDTH = 14;
const HEIGHT = 4;

// The control's interaction area offsets are corrected by a couple of pixels to
// account for the stroke of the rendered shape.
const STROKE_CORRECTION = 1;

const CORNER_CONTROLS = ['tl', 'tr', 'bl', 'br'];
const HORIZONTAL_CONTROLS = ['ml', 'mr'];
const VERTICAL_CONTROLS = ['mt', 'mb'];

const POSITIONING_DIRECTIONS = {
  tl: { horizontal: -1, vertical: -1 },
  tr: { horizontal: 1, vertical: -1 },
  bl: { horizontal: -1, vertical: 1 },
  br: { horizontal: 1, vertical: 1 },
  mt: { horizontal: 0, vertical: -1 },
  ml: { horizontal: -1, vertical: 0 },
  mr: { horizontal: 1, vertical: 0 },
  mb: { horizontal: 0, vertical: 1 },
};

const RENDERING_DIRECTIONS = {
  tl: { horizontal: 1, vertical: 1 },
  tr: { horizontal: -1, vertical: 1 },
  bl: { horizontal: 1, vertical: -1 },
  br: { horizontal: -1, vertical: -1 },
  mt: { horizontal: 1, vertical: 1 },
  mb: { horizontal: 1, vertical: -1 },
  ml: { horizontal: 1, vertical: -1 },
  mr: { horizontal: -1, vertical: -1 },
};

const scaleMap: Record<string, string> = {
  e: 'ew',
  se: 'nwse',
  s: 'ns',
  sw: 'nesw',
  w: 'ew',
  nw: 'nwse',
  n: 'ns',
  ne: 'nesw',
};

const cropScaleCursorHandler = (eventData, control, fabricObject) => {
  const cursor = fabric.controlsUtils.scaleCursorStyleHandler(
    eventData,
    control,
    fabricObject
  );
  const cursorRegex = /^(.{1,2})-resize$/;
  const match = cursor.match(cursorRegex);

  if (!match) {
    return '';
  }

  return `${scaleMap[match[1]]}-resize`;
};

class CropControl extends fabric.Control {
  // Name of the action the control is used for.
  actionName: string;

  // Indicates the corner the control corresponds to.
  position: string;

  // Used for indicating if the control should be rendered right (1) or left (-1) of the
  // shape's center.
  horizontalPositioningDirection: number;

  // Used for indicating if the render direction is from left to right (1) or
  // right to left (-1)
  horizontalRenderingDirection: number;

  // Used for indicating if the control should be rendered above (-1) or
  // below (1) of the shape's center
  verticalPositioningDirection: number;

  // Used for indicating if the render direction of should be from top to
  // bottom (1) or bottom to top (-1)
  verticalRenderingDirection: number;

  constructor(position: string) {
    super();
    this.position = position;

    // There are two directions for each vertical and horizontal offset,
    // positioning and rendering.
    // Positioning direction indicates the direction the
    // control must be moved in relation to the center of the shape, left and
    // up are negative and right and down are positive.
    const positioningDirections = POSITIONING_DIRECTIONS[position];
    this.horizontalPositioningDirection = positioningDirections.horizontal;
    this.verticalPositioningDirection = positioningDirections.vertical;

    // Rendering direction indicates the direction the canvas rendering will be
    // carried out, in relation to the corner where the control is rendered.
    // These are opposite of the positioning directions, left to right and
    // and top to bottom are positive, and right to left and bottom to top are
    // negative.
    const renderingDirections = RENDERING_DIRECTIONS[position];
    this.horizontalRenderingDirection = renderingDirections.horizontal;
    this.verticalRenderingDirection = renderingDirections.vertical;

    this.x = 0.5 * this.horizontalPositioningDirection;
    this.y = 0.5 * this.verticalPositioningDirection;

    this.sizeX =
      this.sizeY =
      this.touchSizeX =
      this.touchSizeY =
        WIDTH + STROKE_CORRECTION;

    if (CORNER_CONTROLS.includes(this.position)) {
      this.actionHandler = fabric.controlsUtils.scalingEqually;
    } else if (HORIZONTAL_CONTROLS.includes(this.position)) {
      this.actionHandler = fabric.controlsUtils.scalingX;
    } else if (VERTICAL_CONTROLS.includes(this.position)) {
      this.actionHandler = fabric.controlsUtils.scalingY;
    }
    this.cursorStyleHandler = cropScaleCursorHandler;
  }

  drawLine(ctx: CanvasRenderingContext2D, left: number, top: number) {
    const { horizontalRenderingDirection, verticalRenderingDirection } = this;

    let lineWidth: number;
    let lineHeight: number;

    let correctionFactor: number;

    let offsetX: number;
    let offsetY: number;

    let offsetCorrectedLeft = left;
    let offsetCorrectedTop = top;

    if (HORIZONTAL_CONTROLS.includes(this.position)) {
      lineWidth = horizontalRenderingDirection * HEIGHT;
      lineHeight = verticalRenderingDirection * WIDTH;

      correctionFactor = horizontalRenderingDirection * STROKE_CORRECTION;

      offsetX = lineWidth / 2 + correctionFactor;
      offsetY = lineHeight / 2;

      offsetCorrectedLeft -= offsetX;
      offsetCorrectedTop -= offsetY;
    } else {
      lineWidth = horizontalRenderingDirection * WIDTH;
      lineHeight = verticalRenderingDirection * HEIGHT;

      correctionFactor = verticalRenderingDirection * STROKE_CORRECTION;

      offsetX = lineWidth / 2;
      offsetY = lineHeight / 2 + correctionFactor;

      offsetCorrectedLeft -= offsetX;
      offsetCorrectedTop -= offsetY;
    }

    ctx.beginPath();
    ctx.moveTo(offsetCorrectedLeft, offsetCorrectedTop);
    ctx.lineTo(offsetCorrectedLeft + lineWidth, offsetCorrectedTop);
    ctx.lineTo(
      offsetCorrectedLeft + lineWidth,
      offsetCorrectedTop + lineHeight
    );
    ctx.lineTo(offsetCorrectedLeft, offsetCorrectedTop + lineHeight);
    ctx.closePath();
    ctx.stroke();
    ctx.fill();
  }

  drawCorner(ctx: CanvasRenderingContext2D, left: number, top: number) {
    const outerHorizontalOffset = this.horizontalRenderingDirection * WIDTH;
    const innerHorizontalOffset = this.horizontalRenderingDirection * HEIGHT;
    const outerVerticalOffset = this.verticalRenderingDirection * WIDTH;
    const innerVerticalOffset = this.verticalRenderingDirection * HEIGHT;

    const offsetX =
      this.horizontalRenderingDirection * (HEIGHT - STROKE_CORRECTION);
    const offsetY =
      this.verticalRenderingDirection * (HEIGHT - STROKE_CORRECTION);
    const offsetCorrectedLeft = left - offsetX;
    const offsetCorrectedTop = top - offsetY;

    ctx.beginPath();
    ctx.moveTo(offsetCorrectedLeft, offsetCorrectedTop);
    ctx.lineTo(offsetCorrectedLeft + outerHorizontalOffset, offsetCorrectedTop);
    ctx.lineTo(
      offsetCorrectedLeft + outerHorizontalOffset,
      offsetCorrectedTop + innerVerticalOffset
    );

    ctx.lineTo(
      offsetCorrectedLeft + innerHorizontalOffset,
      offsetCorrectedTop + innerVerticalOffset
    );
    ctx.lineTo(
      offsetCorrectedLeft + innerHorizontalOffset,
      offsetCorrectedTop + outerVerticalOffset
    );

    ctx.lineTo(offsetCorrectedLeft, offsetCorrectedTop + outerVerticalOffset);
    ctx.closePath();
    ctx.stroke();
    ctx.fill();
  }

  render(ctx: CanvasRenderingContext2D, left: number, top: number) {
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = '#999999';
    ctx.lineWidth = 3;

    if (CORNER_CONTROLS.includes(this.position)) {
      this.drawCorner(ctx, left, top);
    } else {
      this.drawLine(ctx, left, top);
    }

    ctx.restore();
  }
}
export default CropControl;
